import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Search } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import {
  CatalogReviewActionDialog,
  type CatalogReviewDialogAction,
} from "@/components/catalog/CatalogReviewActionDialog";
import { CatalogReviewQueueRowActions } from "@/components/catalog/CatalogReviewQueueRowActions";
import { catalogApi, type CatalogWorkflowRecord } from "@/lib/api/catalog";
import { useToast } from "@/contexts/ToastContext";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function creatorName(record: CatalogWorkflowRecord): string {
  const creator = record.createdBy;
  if (!creator) return "—";
  const fullName = [creator.firstName, creator.lastName].filter(Boolean).join(" ");
  return fullName || creator.email || "—";
}

export default function CatalogReviewQueuePage() {
  const toast = useToast();
  const [records, setRecords] = useState<CatalogWorkflowRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogAction, setDialogAction] = useState<CatalogReviewDialogAction | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<CatalogWorkflowRecord | null>(null);

  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await catalogApi.listReviewQueue();
      setRecords(response.data);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Failed to load review queue.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const filteredRecords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return records;
    return records.filter((record) => {
      const haystack = [
        record.title,
        record.isbn,
        creatorName(record),
        record.authors?.join(" ") ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [records, searchQuery]);

  const handleApprove = async (record: CatalogWorkflowRecord) => {
    try {
      setApprovingId(record.id);
      setActionLoading(true);
      await catalogApi.approveBiblio(record.id);
      toast.success(`"${record.title}" approved and published.`);
      await loadQueue();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to approve record.");
    } finally {
      setActionLoading(false);
      setApprovingId(null);
    }
  };

  const openDialog = (record: CatalogWorkflowRecord, action: CatalogReviewDialogAction) => {
    setSelectedRecord(record);
    setDialogAction(action);
  };

  const handleDialogConfirm = async (value: string) => {
    if (!selectedRecord || !dialogAction) return;
    try {
      setActionLoading(true);
      if (dialogAction === "return") {
        await catalogApi.returnBiblio(selectedRecord.id, value);
        toast.success(`"${selectedRecord.title}" returned for correction.`);
      } else {
        await catalogApi.rejectBiblio(selectedRecord.id, value);
        toast.success(`"${selectedRecord.title}" rejected.`);
      }
      setDialogAction(null);
      setSelectedRecord(null);
      await loadQueue();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to update review status.");
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo<DataTableColumn<CatalogWorkflowRecord>[]>(
    () => [
      {
        id: "title",
        header: "Title",
        cell: (record) => (
          <div>
            <p className="font-medium text-gray-900">{record.title}</p>
            <p className="text-xs text-gray-500">{record.authors?.join(", ") || "No authors"}</p>
          </div>
        ),
      },
      {
        id: "createdBy",
        header: "Submitted by",
        cell: (record) => creatorName(record),
      },
      {
        id: "submittedAt",
        header: "Submitted",
        cell: (record) => (record.submittedAt ? formatDate(record.submittedAt) : "—"),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (record) => (
          <CatalogReviewQueueRowActions
            record={record}
            disabled={actionLoading}
            approving={approvingId === record.id}
            onApprove={handleApprove}
            onOpenDialog={openDialog}
          />
        ),
      },
    ],
    [actionLoading, approvingId]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalog review queue"
        description="Records submitted for approval land here. Approve to publish, return so the cataloger can correct the draft, or reject the submission."
        showSearch={false}
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search pending records..."
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredRecords}
        rowKey={(record) => record.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<ClipboardList className="h-6 w-6 text-slate-400" />}
            title={
              searchQuery.trim() && records.length > 0
                ? "No matching records"
                : "Review queue is empty"
            }
            description={
              searchQuery.trim() && records.length > 0
                ? "Try adjusting your search terms."
                : "Submitted catalog records awaiting approval will appear here."
            }
          />
        }
      />

      <CatalogReviewActionDialog
        open={dialogAction !== null}
        action={dialogAction}
        loading={actionLoading}
        title={
          selectedRecord
            ? `${dialogAction === "reject" ? "Reject" : "Return"} "${selectedRecord.title}"`
            : undefined
        }
        onOpenChange={(open) => {
          if (!open) {
            setDialogAction(null);
            setSelectedRecord(null);
          }
        }}
        onConfirm={handleDialogConfirm}
      />
    </div>
  );
}
